/**
 * The chip-shape half of design rule 2 (#11426 gaps-v6 item 11).
 *
 * A field earns the CHIP shape only when its vocabulary is closed —
 * product-defined —, short (roughly 2–8 values, hard ceiling 12), and
 * repeats across rows. The practical test: could you group a board by it?
 * Status, Stage, Outcome, Relationship, Document type, Interaction type —
 * yes. Domicile is closed but runs to ~20 values, so it is plain text:
 * past ~12 nobody learns the set.
 *
 * Colour is a second decision a field can only take after earning the
 * chip (see enumTone.ts). The one exception runs the other way: when the
 * org has configured colours for a field's options, that choice earns
 * the chip at any size (#12143). Links to records never reach here — a relation
 * is an identity, not a classification, and renders as ink with the link
 * affordance on hover (RelationRunCell).
 *
 * The decision reads the field's DECLARED vocabulary and kind — never a
 * hand-maintained list of column names.
 */

export const CHIP_VOCABULARY_CEILING = 12;

export interface ChipVocabularySource {
  is_enum: boolean;
  is_hierarchical: boolean;
  scalar_values: unknown[] | null;
  /** Present marks the kind's status field — a judgement vocabulary. */
  status_states?: Record<string, string> | null;
  // Synthesized schemas (the review grid's Subtype) don't ship
  // scalar_values but still declare the vocabulary per value through the
  // org's tone maps — those keys are the declared vocabulary too.
  enum_colors?: Record<string, string> | null;
  category_names?: Record<string, string> | null;
}

/** How many values the field DECLARES, wherever the declaration lives. */
function declaredVocabularySize(fact: ChipVocabularySource): number {
  if (Array.isArray(fact.scalar_values) && fact.scalar_values.length > 0) {
    return fact.scalar_values.length;
  }
  const toned = new Set([
    ...Object.keys(fact.enum_colors ?? {}),
    ...Object.keys(fact.category_names ?? {}),
  ]);
  return toned.size;
}

export function earnsChip(fact: ChipVocabularySource): boolean {
  // A judgement field maps onto the five semantic states; the status
  // chip is the one colour mark the row carries.
  if (fact.status_states) return true;
  // A hierarchy is the org's own closed tree (asset factors): the board
  // groups by it and its values render as tags. The ceiling doesn't
  // apply — a path's leaf is read against its ancestry, not against a
  // flat list someone must learn.
  if (fact.is_enum && fact.is_hierarchical) return true;
  // An org that configured a color for its options asked to see them
  // (#12143). The ceiling guards against a colour code nobody chose; a
  // configured map is that choice, so it earns the chip at any size.
  if (fact.enum_colors && Object.keys(fact.enum_colors).length > 0) {
    return true;
  }
  const size = declaredVocabularySize(fact);
  return size > 0 && size <= CHIP_VOCABULARY_CEILING;
}
